import { useState, useEffect, useRef } from "react";
import { ChromePicker } from 'react-color';
import {
  getPlans,
  addCompany,
  updateCompany,
  getCompany,
  complianceCountryForIso,
  complianceCountryChangeRefusal,
  toComplianceCountry,
  ComplianceCountryChange,
  getStatutoryCatalogueCountryIds,
} from "../../../../api/General/Company";
import {
  SubscriptionPlan,
  CompanyInput,
  CompanyUpdateInput,
  ComplianceCountry,
  CountryView,
  OrganizationView,
  CompanyView,
  TimeZoneInfoView,
  Roles,
} from "../../../../models/general";
import { getCountries, getTimeZones } from "../../../../api/General/Country";
import { useTranslation } from "react-i18next";
import { useFileUploadLimits } from "../../../../hooks/useFileUploadLimits";
import { getRoles } from "../../../../api/General/account";
import { lightOrDark } from "../../../../utils";
import PhoneInput from 'react-phone-number-input';
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { GetOrganizations } from "../../../../api/General/organization";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import AppIcon from "../../../../components/AppIcon";
import { Spinner } from "../../../../components/ui/Spinner";
import { Alert } from "../../../../components/ui/Alert";
import { ComplianceCountryChangeNotice, StatutoryCopiesNote } from "../../General/generalDetails";
import OperatorMfaPolicyPanel from "../OperatorMfaPolicyPanel";
import SessionRevocationPanel from "../SessionRevocationPanel";

export default function AddEditCompany() {
  const { t } = useTranslation();
  const role = useSelector((s: any) => s.user.role);
  const params = useParams();
  const navigate = useNavigate();
  const { validateFileSize } = useFileUploadLimits();
  const ref = useRef<HTMLInputElement>(null);
  const id = Number(params.id);

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [countries, setCountries] = useState<CountryView[]>([]);
  const [timeZones, setTimeZones] = useState<TimeZoneInfoView[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationView[]>([]);
  const [input, setInput] = useState<CompanyInput>({
    organizationId: 0,
    countryId: 0,
    websiteLink: "",
    address: "",
    email: "",
    rolesMaxUsers: {},
    phoneNumber: "",
    image: null,
    name: "",
    planId: 0,
    primaryColor: "#304EA1",
    timeZone: "",
    locales: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
  });
  const [company, setCompany] = useState<CompanyView>({} as CompanyView);
  const [roles, setRoles] = useState<string[]>([]);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [success, setSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // CO-879 - consent to a compliance-country change (see ComplianceCountryChangeNotice).
  // What the SERVER holds for the company, as far as this screen knows: the country and the payroll
  // rules it runs on today. Loaded with the page (getCompany), replaced by every save, and by a
  // refusal that says otherwise. `compliance` is read, never derived from the country: a company
  // created before CO-13 can sit in the UAE / Saudi Arabia and still be on "None".
  const [stored, setStored] = useState<{ countryId: number; compliance: ComplianceCountry }>({
    countryId: 0,
    compliance: "None",
  });
  const [countryChangeAccepted, setCountryChangeAccepted] = useState(false);
  // What the server said the change is, when it refused a save this screen thought needed no consent.
  const [serverCountryChange, setServerCountryChange] = useState<ComplianceCountryChange | null>(null);
  // That refusal showed the stored country is not the one this page loaded (changed elsewhere).
  const [countryMovedElsewhere, setCountryMovedElsewhere] = useState(false);
  // Counts blocked / refused saves; each one moves focus to the confirmation.
  const [countryChangeRefusals, setCountryChangeRefusals] = useState(0);
  const countryChangeAcceptRef = useRef<HTMLInputElement>(null);
  // ST-DIALOG - the countries the platform catalogue has statutory leave rows for (Operator read).
  const [statutoryCatalogueCountryIds, setStatutoryCatalogueCountryIds] = useState<number[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (id && role === Roles.Operator) getStatutoryCatalogueCountryIds().then(setStatutoryCatalogueCountryIds);
  }, [id, role]);

  useEffect(() => {
    if (countryChangeRefusals === 0) return;
    const accept = countryChangeAcceptRef.current;
    accept?.scrollIntoView({ block: "center" });
    accept?.focus({ preventScroll: true });
  }, [countryChangeRefusals]);

  const fetchData = async () => {
    setLoading(true);
    if (id) {
      const companyRes = await getCompany(id);
      setInput({
        ...companyRes.payload,
        image: null,
        organizationId: companyRes.payload.organization?.id,
        planId: companyRes.payload.subscriptionPlanView?.id,
        countryId: companyRes.payload.country?.id,
        adminEmail: "",
        adminFirstName: "",
        adminLastName: "",
      });
      setCompany(companyRes.payload);
      setStored({
        countryId: companyRes.payload.country?.id ?? 0,
        // The ISO mapping is only the fallback for a payload without the field.
        compliance:
          toComplianceCountry(companyRes.payload.complianceCountry) ??
          complianceCountryForIso(companyRes.payload.country?.iso),
      });
    }

    const [plansRes, countriesRes, timeZonesRes, rolesRes, orgsRes] = await Promise.all([
      getPlans(),
      getCountries(),
      getTimeZones(),
      getRoles(),
      GetOrganizations(),
    ]);

    setPlans(plansRes.payload);
    setCountries(countriesRes.payload);
    setTimeZones(timeZonesRes.payload);
    setRoles(rolesRes.payload || []);
    setOrganizations(orgsRes.payload);
    setLoading(false);
  };

  const handleImageChange = (event: React.FormEvent<HTMLInputElement>) => {
    const f = (event.target as any).files[0];
    if (f) {
      const { valid, maxMB } = validateFileSize(f, 'companyLogoMaxSizeMB');
      if (!valid) {
        alert(t('fileSizeExceedsLimit', { name: f.name, maxMB }));
        (event.target as any).value = '';
        return;
      }
    }
    setInput((prev) => ({ ...prev, image: f }));
  };

  const addNewCompany = async () => {
    try {
      setLoading(true);
      input.rolesMaxUsers = input.rolesMaxUsers || {};
      const result = await addCompany(input);
      setInput({
        organizationId: 0, countryId: 0, websiteLink: "", address: "", email: "",
        phoneNumber: "", image: null, rolesMaxUsers: {}, name: "", planId: 1,
        primaryColor: "#304EA1", timeZone: "", locales: "",
        adminFirstName: "", adminLastName: "", adminEmail: "",
      });
      setLoading(false);
      return result.payload.id;
    } catch {
      setLoading(false);
    }
    return undefined;
  };

  // CO-879 - the server re-derives the payroll rules only when the request carries a country
  // DIFFERENT from the stored one, or the confirmation flag. So this screen sends the country only
  // when the Operator changed it, and an untouched country never blocks a save and never switches
  // anything: raising a seat cap leaves the company's country and payroll rules exactly as stored.
  //
  // `pendingCountryChange` is the change that needs consent: the Operator picked a country on other
  // rules, or the server refused a save (its 409 lands in `serverCountryChange` and wins). Editing
  // only: a new company has no rules to switch away from.
  //
  // Not offered here, on purpose: the optional switch Settings > General shows a company that sits in
  // the UAE / Saudi Arabia but is still on the general rules. That is the company's Hr decision.
  const selectedCountryId = input.countryId ?? 0;
  const selectedCountry = countries.find(c => c.id === selectedCountryId);
  const selectedCompliance = selectedCountry ? complianceCountryForIso(selectedCountry.iso) : undefined;
  const countryChanged = selectedCountryId !== stored.countryId;
  // ST-DIALOG - says the new country's statutory leave minimums will be added to this company's
  // Statutory Templates (spec section 5.2): inside the confirmation when there is one, else on its own.
  const statutoryCopiesLine =
    id && countryChanged && selectedCountry && statutoryCatalogueCountryIds.includes(selectedCountry.id)
      ? t("settings.general.countryChange.statutoryCopiesOperator", {
          defaultValue: "The statutory leave minimums of {{country}} will be added to this company's Statutory Templates.",
          country: selectedCountry.name,
        })
      : undefined;
  let pendingCountryChange: { from: ComplianceCountry; to: ComplianceCountry } | null = null;
  if (id && serverCountryChange) {
    pendingCountryChange = {
      from: serverCountryChange.from ?? stored.compliance,
      to: serverCountryChange.to ?? selectedCompliance ?? stored.compliance,
    };
  } else if (id && countryChanged && selectedCompliance !== undefined && selectedCompliance !== stored.compliance) {
    pendingCountryChange = { from: stored.compliance, to: selectedCompliance };
  }
  const switchingPayrollRules = pendingCountryChange != null && countryChangeAccepted;

  const resetCountryChangeConsent = () => {
    setCountryChangeAccepted(false);
    setServerCountryChange(null);
    setCountryMovedElsewhere(false);
    setCountryChangeRefusals(0);
  };

  // Consent is given to ONE change, so picking another country withdraws it.
  const selectCountry = (nextCountryId: number) => {
    setInput(prev => ({ ...prev, countryId: nextCountryId }));
    resetCountryChangeConsent();
  };

  const handleSaveFailure = (failure: unknown) => {
    const refusal = complianceCountryChangeRefusal(failure);
    if (refusal) {
      // The refusal names what the server holds today (country and payroll rules). That is the
      // truth, so it replaces what this page loaded: "from" is then right, and the keep button
      // appears and puts back the country the server really has. null = the company has no country.
      const serverCountryId = refusal.previousCountryId === null ? 0 : refusal.previousCountryId;
      if (serverCountryId !== undefined && serverCountryId !== stored.countryId) {
        setCountryMovedElsewhere(true);
      }
      setStored(prev => ({
        countryId: serverCountryId ?? prev.countryId,
        compliance: refusal.from ?? prev.compliance,
      }));
      setServerCountryChange(refusal);
      setCountryChangeAccepted(false);
      setCountryChangeRefusals(n => n + 1);
      return;
    }
    setSaveFailed(true);
  };

  const updateOldCompany = async () => {
    if (!id) return;
    setSuccess(false);
    setSaveFailed(false);
    if (pendingCountryChange && !countryChangeAccepted) {
      // Not sent at all: the server would refuse it, and asking here keeps the form as typed.
      // Only a CHANGED country (or a server refusal) gets here.
      setCountryChangeRefusals(n => n + 1);
      return;
    }
    // The country goes out only when the Operator changed it (a ticked confirmation implies that).
    // Left out, the server keeps the stored country AND the stored payroll rules.
    const sendCountry = countryChanged || switchingPayrollRules;
    setLoading(true);
    try {
      // PUT Company/{id} is a partial update (CO-879): a field left out keeps its stored value, a
      // text field sent as "" is cleared. So send ONLY what this screen lets the Operator edit. The
      // state also holds the rest of the loaded CompanyView (address, website, MOL / CR / Mudad /
      // Qiwa numbers, bank routing code...), and sending those back would overwrite whatever the
      // company's own Hr saved since this page was opened.
      const update: CompanyUpdateInput = {
        name: input.name,
        image: input.image,
        email: input.email,
        organizationId: input.organizationId,
        countryId: sendCountry ? input.countryId : undefined,
        // PhoneInput hands back undefined when emptied, which would mean "keep".
        phoneNumber: input.phoneNumber ?? "",
        timeZone: input.timeZone,
        rolesMaxUsers: input.rolesMaxUsers,
        // undefined when the company has no plan, which leaves the plan as it is.
        planId: input.planId,
        primaryColor: input.primaryColor,
        locales: input.locales,
        confirmComplianceCountryChange: switchingPayrollRules ? true : undefined,
      };
      const result = await updateCompany(id, update);
      if (result?.errors?.length) {
        handleSaveFailure(result);
        return;
      }
      // The saved row is the truth; what this request asked for is only the fallback for a payload
      // without the field.
      const saved = result?.payload;
      const savedCompliance =
        toComplianceCountry(saved?.complianceCountry) ??
        (switchingPayrollRules ? pendingCountryChange?.to : undefined) ??
        stored.compliance;
      const savedCountryId =
        saved?.countryId ?? saved?.country?.id ?? (sendCountry ? selectedCountryId : stored.countryId);
      setStored({ countryId: savedCountryId, compliance: savedCompliance });
      // An untouched country was not sent. If it was changed elsewhere since this page loaded, the
      // server kept THAT one: show it, or the next save would send the old country as a change.
      if (!sendCountry) setInput(prev => ({ ...prev, countryId: savedCountryId }));
      resetCountryChangeConsent();
      setSuccess(true);
    } catch (error) {
      handleSaveFailure(error);
    } finally {
      setLoading(false);
    }
  };

  const validateInputs = () => {
    return !(input.countryId == null || input.countryId === 0 || input.name == null || input.name === "");
  };

  const addOrUpdate = async () => {
    if (validateInputs()) {
      if (id) {
        await updateOldCompany();
      } else {
        roles.map((r) => (input.rolesMaxUsers[r] = input.rolesMaxUsers[r] || 0));
        await addNewCompany();
        navigate("/settings/companies");
      }
    }
  };

  const { rolesMaxUsers } = input;
  const { rolesUsersAvailable } = company;

  const countryOptions = countries.map(c => ({ value: c.id, label: c.name }));
  const timeZoneOptions = timeZones.map(tz => ({ value: tz.id, label: tz.displayName }));
  const orgOptions = organizations.map(o => ({ value: o.id, label: o.name }));
  const storedCountryName = countries.find(c => c.id === stored.countryId)?.name;

  return (
    <div className="flex-1">
      <div className="flex flex-col gap-5 p-4 items-center">
        {success && (
          <div role="alert" data-testid="company-save-success" className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-700 dark:text-emerald-400">
            {t("Company Updated Successfully")}
          </div>
        )}
        <div className="flex flex-col gap-4 w-full max-w-lg">
          {role === Roles.Operator && (
            <Input
              required
              label={t("Company Name")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, name: e.target.value }))}
              value={input.name}
            />
          )}

          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <Button onClick={() => ref.current?.click()}>{t("Upload Image")}</Button>
              {input.image && (
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm text-foreground">{input.image.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setInput(prev => ({ ...prev, image: null }))}
                  >
                    <AppIcon name="Trash2" size={16} />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <input ref={ref} onChange={handleImageChange} type="file" className="hidden" accept="image/*" />

          <Input
            label={t("Email")}
            type="text"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, email: e.target.value }))}
            value={input.email}
          />

          {role === Roles.Operator && (
            <Select
              label={t("Organization")}
              required
              options={orgOptions}
              value={input.organizationId}
              onChange={(val: any) => setInput(prev => ({ ...prev, organizationId: val || 0 }))}
              searchable
            />
          )}

          <Select
            label={t("Country")}
            required
            options={countryOptions}
            value={input.countryId}
            onChange={(val: any) => selectCountry(val || 0)}
            searchable
            data-testid="company-country"
          />

          {pendingCountryChange && (
            <ComplianceCountryChangeNotice
              ref={countryChangeAcceptRef}
              from={pendingCountryChange.from}
              to={pendingCountryChange.to}
              accepted={countryChangeAccepted}
              onAcceptedChange={setCountryChangeAccepted}
              refused={countryChangeRefusals > 0}
              onKeepCurrent={stored.countryId && countryChanged ? () => selectCountry(stored.countryId) : undefined}
              keepCountryName={storedCountryName}
              changedElsewhereCountryName={countryMovedElsewhere ? storedCountryName : undefined}
              statutoryCopiesLine={statutoryCopiesLine}
              disabled={loading}
            />
          )}

          {!pendingCountryChange && statutoryCopiesLine && <StatutoryCopiesNote line={statutoryCopiesLine} />}

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">{t('Phone Number')}</label>
            <PhoneInput
              international
              dir="ltr"
              className="h-[30px]"
              value={input.phoneNumber}
              onChange={(ev: any) => setInput(prev => ({ ...prev, phoneNumber: ev }))}
            />
          </div>

          <Select
            label={t("TimeZone")}
            required
            options={timeZoneOptions}
            value={input.timeZone}
            onChange={(val: any) => setInput(prev => ({ ...prev, timeZone: val || '' }))}
            searchable
          />

          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-foreground">
              {t("Licensed Users")} <span className="text-destructive">*</span>
            </label>
            <div className="rounded border border-border p-1">
              <div className="grid grid-cols-2 gap-4" dir="ltr">
                {roles.map((r) => (
                  <div key={r} className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground mb-1">{r}</span>
                    <div className="flex items-end">
                      {id ? (
                        <span className="text-sm text-foreground mb-1">
                          {`${rolesUsersAvailable?.[r] ?? 0}  /`}
                        </span>
                      ) : null}
                      {role === Roles.Operator ? (
                        <input
                          type="number"
                          min="0"
                          placeholder={t(r) + " " + t("Users")}
                          className="m-0.5 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          onChange={(ev) => {
                            const value = parseInt(ev.target.value || '0');
                            setInput(prev => ({
                              ...prev,
                              rolesMaxUsers: { ...prev.rolesMaxUsers, [r]: value },
                            }));
                          }}
                          value={rolesMaxUsers[r] as any}
                        />
                      ) : (
                        <span className="text-sm text-foreground mb-1">{rolesMaxUsers[r]}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {role === Roles.Operator && (
            <Input
              label={t("Locales")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, locales: e.target.value }))}
              value={input.locales}
            />
          )}

          {!id && role === Roles.Operator && (
            <>
              <Input
                required
                label={t("Admin First Name")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, adminFirstName: e.target.value }))}
                value={input.adminFirstName}
              />
              <Input
                required
                label={t("Admin Last Name")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, adminLastName: e.target.value }))}
                value={input.adminLastName}
              />
              <Input
                required
                label={t("Admin Email")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, adminEmail: e.target.value }))}
                value={input.adminEmail}
              />
            </>
          )}

          <h2 className="text-sm font-semibold text-foreground">{t("Primary Color")}</h2>

          <ChromePicker
            color={input.primaryColor}
            onChangeComplete={(color) => {
              if (input.primaryColor !== color.hex && lightOrDark(color.hex) === 'light') {
                setAlertVisible(true);
                setAlertContent(t("Please choose a darker color"));
              } else {
                setAlertVisible(false);
                setInput(prev => ({ ...prev, primaryColor: color.hex }));
              }
            }}
          />

          {alertVisible && (
            <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
              {alertContent}
            </div>
          )}

          {saveFailed && (
            <Alert
              variant="error"
              className="text-sm"
              onClose={() => setSaveFailed(false)}
              closeLabel={t("Close")}
              data-testid="company-save-failed"
            >
              {t("settings.general.saveFailed", "The company details were not saved. Check the fields and try again.")}
            </Alert>
          )}

          <Button
            fullWidth
            variant={switchingPayrollRules ? "destructive" : "default"}
            disabled={loading}
            onClick={addOrUpdate}
            data-testid="company-save"
          >
            {loading ? (
              <Spinner size="sm" />
            ) : switchingPayrollRules ? (
              // Names the outcome once the Operator has agreed to it (ui-standards: destructive actions).
              t("settings.general.countryChange.save", "Save and switch payroll rules")
            ) : (
              t("Submit")
            )}
          </Button>

          {/* CO-323 — per-company MFA policy. Operator only (the API is Policy = "Operator"); saved on its own. */}
          {!!id && role === Roles.Operator && <OperatorMfaPolicyPanel companyId={id} />}

          {/* CO-708 — Operator danger zone: end every session in this company. Uses the SAVED name
              (company.name), not the editable input, because that is what the server confirms against. */}
          {id && role === Roles.Operator && company?.name ? (
            <SessionRevocationPanel scope="company" companyId={id} companyName={company.name} />
          ) : null}
        </div>
      </div>
    </div>
  );
}
